/**
 * CommunicationDashboardPage - Phase 5.3B
 * Compact operational dashboard for Communication Center
 * 
 * Features:
 * - Operational metrics (sent today, queued, failed, retry pending)
 * - Channel distribution
 * - Provider status
 * - Limited charts (max 3)
 * - Quick access to queue and logs
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { API } from '../../../lib/api';
import { toast } from 'sonner';
import {
  MessageSquare, Bell, Mail, Send, AlertTriangle, Clock,
  CheckCircle, XCircle, RotateCcw, RefreshCw, Activity,
  ArrowRight, TrendingUp, TrendingDown, Zap, List
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

// Custom Legend formatter - moved outside component to avoid re-creation on render
const LegendFormatter = (value) => <span className="text-xs">{value}</span>;

// Metric Card Component
const MetricCard = ({ title, value, icon: Icon, color = 'gray', subtext, trend }) => {
  const colorMap = {
    green: 'bg-green-50 border-green-200 text-green-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  };

  const iconColorMap = {
    green: 'text-green-500',
    blue: 'text-blue-500',
    yellow: 'text-yellow-500',
    red: 'text-red-500',
    orange: 'text-orange-500',
    purple: 'text-purple-500',
    gray: 'text-gray-500',
  };

  return (
    <div
      className={`rounded-lg border p-3 ${colorMap[color]}`}
      data-testid={`metric-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${iconColorMap[color]}`} />
          <span className="text-xs font-medium">{title}</span>
        </div>
        {trend !== undefined && (
          <span className={`text-xs flex items-center gap-0.5 ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold mt-1">{value}</p>
      {subtext && <p className="text-xs opacity-70 mt-0.5">{subtext}</p>}
    </div>
  );
};

// Channel Status Indicator
const ChannelStatus = ({ channel, data }) => {
  const channelIcons = {
    whatsapp: MessageSquare,
    push: Bell,
    email: Mail,
  };

  const channelColors = {
    whatsapp: 'text-green-600 bg-green-50',
    push: 'text-purple-600 bg-purple-50',
    email: 'text-blue-600 bg-blue-50',
  };

  const Icon = channelIcons[channel] || MessageSquare;
  const total = (data?.sent || 0) + (data?.failed || 0);
  const successRate = total > 0 ? Math.round((data?.sent / total) * 100) : 100;

  return (
    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${channelColors[channel]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <p className="text-sm font-medium capitalize">{channel}</p>
          <p className="text-xs text-gray-500">
            {data?.sent || 0} inviati / {data?.queued || 0} in coda
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className={`text-sm font-bold ${successRate >= 90 ? 'text-green-600' : successRate >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
          {successRate}%
        </p>
        <p className="text-xs text-gray-500">success</p>
      </div>
    </div>
  );
};

// Provider Status Card
const ProviderStatus = ({ providers }) => {
  if (!providers || providers.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500 text-sm">
        Nessun provider configurato
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {providers.map((provider, i) => (
        <div key={i} className="flex items-center justify-between p-2 border rounded-lg">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${provider.healthy ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="text-sm font-medium">{provider.name}</span>
            <span className="text-xs text-gray-500 capitalize">{provider.type}</span>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            provider.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {provider.enabled ? 'Attivo' : 'Disabilitato'}
          </span>
        </div>
      ))}
    </div>
  );
};

// Main Dashboard Component
export default function CommunicationDashboardPage() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [providers, setProviders] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = useCallback(async () => {
    if (!token) return;

    try {
      // Fetch dashboard stats
      const [statsRes, providersRes] = await Promise.all([
        fetch(`${API}/admin/communication/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/admin/communication/providers`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);

        // Prepare chart data - Channel distribution
        const channelData = Object.entries(statsData.by_channel || {}).map(([channel, data]) => ({
          name: channel.charAt(0).toUpperCase() + channel.slice(1),
          sent: data.sent || 0,
          failed: data.failed || 0,
          queued: data.queued || 0,
        }));
        setChartData(channelData);
      }

      if (providersRes.ok) {
        const providersData = await providersRes.json();
        setProviders(providersData?.providers || providersData || []);
      }

      setLastUpdate(new Date());
    } catch (e) {
      console.error('Error fetching dashboard data:', e);
      toast.error('Errore nel caricamento della dashboard');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Chart colors
  const COLORS = ['#22c55e', '#ef4444', '#eab308'];

  // Pie chart data for channel distribution
  const pieData = chartData.map(d => ({
    name: d.name,
    value: d.sent + d.failed,
  })).filter(d => d.value > 0);

  const CHANNEL_COLORS = {
    Whatsapp: '#25D366',
    Push: '#9333EA',
    Email: '#3B82F6',
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-4 max-w-7xl mx-auto" data-testid="communication-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Communication Dashboard</h1>
            <p className="text-sm text-gray-500">
              Ultimo aggiornamento: {lastUpdate?.toLocaleTimeString('it-IT')}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Aggiorna
            </Button>
            <Link to="/admin/communication/queue">
              <Button variant="outline" size="sm">
                <List className="w-4 h-4 mr-1" />
                Coda
              </Button>
            </Link>
            <Link to="/admin/communication/logs">
              <Button variant="outline" size="sm">
                <Activity className="w-4 h-4 mr-1" />
                Log
              </Button>
            </Link>
          </div>
        </div>

        {/* Key Metrics Row - Compact */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <MetricCard
            title="Inviati oggi"
            value={stats?.today?.total_sent || 0}
            icon={Send}
            color="green"
          />
          <MetricCard
            title="Programmati oggi"
            value={stats?.today?.scheduled || 0}
            icon={Clock}
            color="blue"
          />
          <MetricCard
            title="In coda"
            value={stats?.today?.queued || 0}
            icon={List}
            color="yellow"
          />
          <MetricCard
            title="Falliti"
            value={stats?.today?.failed || 0}
            icon={XCircle}
            color="red"
          />
          <MetricCard
            title="Retry pending"
            value={stats?.today?.retrying || 0}
            icon={RotateCcw}
            color="orange"
          />
          <MetricCard
            title="Fallback"
            value={stats?.fallback?.count || 0}
            icon={ArrowRight}
            color="purple"
            subtext={stats?.fallback?.rate ? `${stats.fallback.rate.toFixed(0)}% dei routing` : undefined}
          />
        </div>

        {/* Success Rate Banner */}
        <div className={`p-3 rounded-lg flex items-center justify-between ${
          stats?.success_rate >= 95 ? 'bg-green-50 border border-green-200' :
          stats?.success_rate >= 80 ? 'bg-yellow-50 border border-yellow-200' :
          'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center gap-2">
            {stats?.success_rate >= 95 ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : stats?.success_rate >= 80 ? (
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            ) : (
              <XCircle className="w-5 h-5 text-red-600" />
            )}
            <span className="font-medium">
              Tasso di successo: {stats?.success_rate?.toFixed(1) || 100}%
            </span>
          </div>
          <span className="text-sm text-gray-600">
            {stats?.today?.total_sent || 0} su {(stats?.today?.total_sent || 0) + (stats?.today?.failed || 0)} messaggi
          </span>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Channel Status - Left */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Stato Canali
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <ChannelStatus channel="whatsapp" data={stats?.by_channel?.whatsapp} />
              <ChannelStatus channel="push" data={stats?.by_channel?.push} />
              <ChannelStatus channel="email" data={stats?.by_channel?.email} />
            </CardContent>
          </Card>

          {/* Channel Distribution Chart - Center */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Distribuzione Canali</CardTitle>
            </CardHeader>
            <CardContent>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={CHANNEL_COLORS[entry.name] || COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={LegendFormatter}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[180px] flex items-center justify-center text-gray-400 text-sm">
                  Nessun dato disponibile
                </div>
              )}
            </CardContent>
          </Card>

          {/* Success/Failure Trend - Right */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Messaggi per Canale</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={chartData} layout="vertical">
                    <XAxis type="number" hide />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      width={70}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip />
                    <Bar dataKey="sent" fill="#22c55e" name="Inviati" stackId="a" />
                    <Bar dataKey="failed" fill="#ef4444" name="Falliti" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[180px] flex items-center justify-center text-gray-400 text-sm">
                  Nessun dato disponibile
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Provider Status - Compact */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Provider Status
              </span>
              <Link to="/admin/communication/channels" className="text-xs text-blue-600 hover:underline">
                Gestisci →
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {providers.length > 0 ? (
                providers.slice(0, 6).map((provider, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <div className={`w-2 h-2 rounded-full ${provider.health_status === 'healthy' ? 'bg-green-500' : provider.health_status === 'degraded' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{provider.name}</p>
                      <p className="text-xs text-gray-500 capitalize">{provider.type}</p>
                    </div>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      provider.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {provider.enabled ? 'ON' : 'OFF'}
                    </span>
                  </div>
                ))
              ) : (
                <div className="col-span-3 text-center py-4 text-gray-500 text-sm">
                  <MessageSquare className="w-6 h-6 mx-auto mb-2 text-gray-300" />
                  Nessun provider configurato
                  <Link to="/admin/communication/channels" className="block mt-1 text-blue-600 hover:underline text-xs">
                    Configura ora →
                  </Link>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/communication/simulation">
            <Button variant="outline" size="sm">
              <Zap className="w-4 h-4 mr-1" />
              Simulazione
            </Button>
          </Link>
          <Link to="/admin/communication/templates">
            <Button variant="outline" size="sm">
              <MessageSquare className="w-4 h-4 mr-1" />
              Template
            </Button>
          </Link>
          <Link to="/admin/communication/rules">
            <Button variant="outline" size="sm">
              <Activity className="w-4 h-4 mr-1" />
              Regole
            </Button>
          </Link>
          <Link to="/admin/communication/routing">
            <Button variant="outline" size="sm">
              <ArrowRight className="w-4 h-4 mr-1" />
              Routing
            </Button>
          </Link>
          <Link to="/admin/communication/settings">
            <Button variant="outline" size="sm">
              Impostazioni
            </Button>
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
}
